import React from 'react'
import _ from 'lodash'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import { withTranslation } from 'react-i18next'
import { VectorMap } from 'react-jvectormap'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons'
import { rhythm, media, color } from '../../utils/typography'

import SEO from '../../components/seo'
import Container from '../../components/container'
import Link from '../../components/link'

import './map.sass'

export const query = graphql`
  query {
    countries: allMarkdownRemark(
      filter: { frontmatter: { type: { eq: "country" } } }
    ) {
      edges {
        node {
          frontmatter {
            code
            treaties {
              type
              dest
              suspension
            }
          }
        }
      }
    }
  }
`

export default styled(withTranslation()(class extends React.PureComponent {
  state = {
    series: { CN: 3 },
    countries: [],
  }

  componentDidMount = () => {
    const { data, t } = this.props
    const series = { CN: 3 }

    const countries = _.compact(_.map(data.countries.edges, edge => {
      const country = edge.node.frontmatter

      const hasHkExtradition = _.find(country.treaties, treaty =>
        treaty.type === 'extradition' && treaty.dest === 'hk' && !treaty.suspension)
      const hasHkAssistance = _.find(country.treaties, treaty =>
        treaty.type === 'assistance' && treaty.dest === 'hk' && !treaty.suspension)
      const hasCnExtradition = _.find(country.treaties, treaty =>
        treaty.type === 'extradition' && treaty.dest === 'cn' && !treaty.suspension)
      const hasCnAssistance = _.find(country.treaties, treaty =>
        treaty.type === 'assistance' && treaty.dest === 'cn' && !treaty.suspension)
      const hasExtradition = hasHkExtradition || hasCnExtradition
      const hasAssistance = hasHkAssistance || hasCnAssistance

      let title, klass, level
      const texts = []

      if (!hasExtradition && !hasAssistance) { return null }

      if (hasExtradition && hasAssistance) {
        title = t('extradition.map.both.title')
        klass = 'danger'
        level = 2
      }

      if (hasExtradition && !hasAssistance) {
        title = t('extradition.map.extradition.title')
        klass = 'danger'
        level = 2
      }

      if (!hasExtradition && hasAssistance) {
        title = t('extradition.map.assistance.title')
        klass = 'warn'
        level = 1
      }

      if (hasHkExtradition && hasHkAssistance)
        texts.push(t('extradition.map.both.text', { dest: t('countries.hk.short') }))
      if (hasHkExtradition && !hasHkAssistance)
        texts.push(t('extradition.map.extradition.text', { dest: t('countries.hk.short') }))
      if (!hasHkExtradition && hasHkAssistance)
        texts.push(t('extradition.map.assistance.text', { dest: t('countries.hk.short') }))

      if (hasCnExtradition && hasCnAssistance)
        texts.push(t('extradition.map.both.text', { dest: t('countries.cn.short') }))
      if (hasCnExtradition && !hasCnAssistance)
        texts.push(t('extradition.map.extradition.text', { dest: t('countries.cn.short') }))
      if (!hasCnExtradition && hasCnAssistance)
        texts.push(t('extradition.map.assistance.text', { dest: t('countries.cn.short') }))

      if (level) series[country.code.toUpperCase()] = level

      return {
        code: country.code,
        name: t(`countries.${country.code}.short`),
        title, klass, texts, level,
      }
    }))

    this.setState({ countries, series })
  }

  handleHover = (ev, el, _code) => {
    const { t } = this.props
    const code = _code.toLowerCase()
    if (code === 'cn') return el.html(t('extradition.map.china'))
    const country = _.find(this.state.countries, c => c.code === code)
    if (country) {
      el.html(`
        ${country.name} - <span class='${country.klass}'>${country.title}</span>
        <br/> ${country.texts.join('<br/>')}
      `)
    }
  }

  render() {
    const { className, t } = this.props
    return (
      <div className={className}>
        <SEO title={t('extradition.map.title')} />

        <Helmet>
          <link rel='stylesheet' type='text/css' media='screen'
            href='//cdnjs.cloudflare.com/ajax/libs/jvectormap/2.0.4/jquery-jvectormap.css' />
        </Helmet>

        <header>
          <Container>
            <div className='container'>
              <Link className='back' to='/extradition'>
                <FontAwesomeIcon icon={faAngleLeft} />
                <span className='back-text'>{' '}{t('back')}</span>
              </Link>

              <h1>{t('extradition.map.title')}</h1>
            </div>
          </Container>
        </header>

        {/* will only be available during client side rendering */}
        {VectorMap && <VectorMap map={'world_mill'}
          containerClassName='map-container'
          backgroundColor='#fdda24' zoomOnScroll={true}
          regionStyle={{
            initial: {
              fill: '#ffffff',
              'fill-opacity': 1,
              stroke: 'none',
              'stroke-width': 0,
              'stroke-opacity': 0,
            },
            hover: {
              'fill-opacity': 0.85,
              cursor: 'pointer',
            },
            selected: {
              'fill-opacity': 0.7,
            },
          }}
          regionsSelectable={true}
          series={{
            regions: [{
              values: this.state.series,
              scale: ['#de8787', '#c83737', '#000000'],
              normalizeFunction: 'polynomial',
            }],
          }}
          onRegionTipShow={this.handleHover} />}
      </div>
    )
  }
}))`
  header {
    text-align: center;
    height: ${rhythm(3/2 + 2/3)};
    padding: ${rhythm(1/3)} 0;

    h1, .back {
      font-size: ${rhythm(2/3)};
      line-height: ${rhythm(3/2)};
      margin: 0;
      padding: 0;
      color: ${color.body};
    }

    h1 {
      ${media.tablet} { font-size: ${rhythm(1)} }
    }

    .container {
      position: relative;
    }

    .back {
      position: absolute;
      top: 0;
      left: 0;
      text-transform: lowercase;
    }

    .back-text {
      margin-left: ${rhythm(1/6)};
      display: none;
      ${media.tablet} { display: inline-block; }
    }
  }

  .map-container {
    width: 100%;
    height: calc(100vh - ${rhythm(3/2)} - ${rhythm(2/3)});
    padding: ${rhythm(1/6)};
  }
`
